import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { BriefResume } from 'builder/modules/panel'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'

import { Header } from '../../../components/Header/Header'
import { StepItems } from '../../../components/StepItems/StepItems'
import { StepsActions } from '../../../components/StepsActions/StepsActions'

import SuccessIcon from '../../../assets/success-icon.svg'
import ScanningDesktop from '../../../assets/Scanning_Desktop.webm'
import ScanningMobile from '../../../assets/Scanning_Mobile.webm'
import ScanningTablet from '../../../assets/Scanning_Tablet.webm'

import { Steps, Variant } from '../../../types'
import { ContentContainer, Box } from '../../styles'
import { useGetConfig } from './useGetConfig'
import * as Styles from './styles'

type Props = {
  step: Steps
  variant: Variant
  nextStep?: Steps
  resume?: BriefResume
  isAiResumeLoading: boolean
  setStep: (step: Steps) => void
}

export const WelcomeStep = ({
  step,
  variant,
  nextStep,
  setStep,
  isAiResumeLoading,
  resume,
}: Props) => {
  const { steps, header, actions } = useGetConfig(variant)
  const { isPhone, isTablet } = useMediaQueries()

  const getThumbnailUrl = () => {
    if (!resume) return
    return formatDocumentPreviewUrl(resume)
  }

  const getSrc = () => {
    if (isPhone) return ScanningMobile
    if (isTablet) return ScanningTablet
    return ScanningDesktop
  }

  const ActionsContainer = (
    <Styles.ActionsContainer data-testid="welcome-step-actions">
      <StepsActions
        variant={variant}
        nextStep={nextStep}
        activeStep={step}
        setStep={setStep}
        backButtonText={actions.back}
        continueButtonText={actions.continue}
      />
    </Styles.ActionsContainer>
  )

  useEffect(() => {
    trackInternalEvent('leads_onboarding_1')
  }, [])

  return (
    <ContentContainer data-testid="welcome-step">
      <Box>
        <Styles.InfoContainer data-testid="welcome-step-info">
          <Header {...header} />
          <Styles.StepsContainer data-testid="welcome-step-items">
            <StepItems items={steps} />
          </Styles.StepsContainer>
          {!isPhone && !isTablet && ActionsContainer}
        </Styles.InfoContainer>
      </Box>
      <Box>
        <Styles.AnimationContainer data-testid="welcome-step-animation">
          {isAiResumeLoading ? (
            <Styles.Video
              src={getSrc()}
              autoPlay
              muted
              loop
              playsInline
              data-testid="welcome-step-loading-video"
            />
          ) : (
            <>
              <Styles.ThumbnailContainer data-testid="welcome-step-thumbnail">
                <Styles.SuccessIcon src={SuccessIcon} data-testid="welcome-step-success-icon" />
                <Styles.Thumbnail
                  src={getThumbnailUrl()}
                  data-testid="welcome-step-resume-thumbnail"
                />
              </Styles.ThumbnailContainer>
              <Styles.ThumbnailOverlayContainer data-testid="welcome-step-overlay">
                <Styles.ThumbnailOverlayGradient />
                <Styles.ThumbnailOverlay />
              </Styles.ThumbnailOverlayContainer>
            </>
          )}
        </Styles.AnimationContainer>
      </Box>
      {(isPhone || isTablet) && ActionsContainer}
    </ContentContainer>
  )
}
